import ScrollReveal from '../ScrollReveal';
import SearchAnimation from './SearchAnimation';
import IllustrationWorkflow from './IllustrationWorkflow';
import PianoAnimation from './PianoAnimation';
import LinkInBioAnimation from './LinkInBioAnimation';
import { t } from '@/data/translations';
import type { Lang } from '@/pages/Index';

const animationMap: Record<string, React.ReactNode> = {
  search: <SearchAnimation />,
  linkInBio: <LinkInBioAnimation />,
  illustration: <IllustrationWorkflow />,
  piano: <PianoAnimation />,
};

interface ProjectItemProps {
  number: string;
  title: string;
  client: string;
  year: string;
  description: string;
  tags: string[];
  url?: string;
  delay?: number;
  animation?: React.ReactNode;
  viewProject: string;
  linkSoon: string;
}

const ProjectItem = ({ number, title, client, year, description, tags, url, delay = 0, animation, viewProject, linkSoon }: ProjectItemProps) => (
  <ScrollReveal delay={delay}>
    <article className="py-8 lg:py-10 relative">
      <span className="text-6xl md:text-7xl font-extralight text-primary/20 leading-none mb-4 block">{number}</span>
      {animation}
      <div className="mt-4">
        <h3 className="text-xl font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground mt-1">{client} • {year}</p>
        <p className="text-base text-muted-foreground mt-4 leading-relaxed">{description}</p>
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="inline-block mt-4 text-sm text-foreground link-hover hover:text-primary transition-colors duration-300">
            {viewProject}
          </a>
        ) : (
          <span className="inline-block mt-4 text-sm text-muted-foreground">{linkSoon}</span>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          {tags.map((tag, index) => (
            <span key={index} className="text-xs px-3 py-1 bg-muted text-muted-foreground rounded-full">{tag}</span>
          ))}
        </div>
      </div>
    </article>
  </ScrollReveal>
);

const Projects = ({ lang }: { lang: Lang }) => {
  const tr = t(lang).projects;
  return (
    <section id="projects" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-4xl mx-auto">
        <ScrollReveal>
          <div className="section-divider mb-8" />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <h2 className="text-sm tracking-widest uppercase text-muted-foreground mb-12">{tr.sectionTitle}</h2>
        </ScrollReveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {tr.items.map((project, index) => (
            <ProjectItem
              key={index}
              {...project}
              animation={animationMap[project.animationKey]}
              delay={(index + 1) * 100}
              viewProject={tr.viewProject}
              linkSoon={tr.linkSoon}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
